// One paywall for the whole console (COL-352). Create chokepoints wrap their
// action in usePaywall().guardCreate(trigger, action):
//   * staff / owners / entitled operators → the action runs, zero change;
//   * unpaid operators → the dialog opens instead;
//   * entitlement still loading → no-op (never flash a paywall at a paying
//     operator; the click simply does nothing for the sub-second load window).
// The server-side gate (RLS operator_can_write) is the enforcement — this is
// the friendly front door.
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useOperatorEntitlement } from "@/hooks/useOperatorEntitlement";
import {
  PaywallDialog,
  type PaywallTrigger,
} from "@/components/paywall/PaywallDialog";

interface PaywallContextType {
  /** Run `action` when entitled; open the paywall dialog otherwise. */
  guardCreate: (trigger: PaywallTrigger, action: () => void) => void;
  openPaywall: (trigger: PaywallTrigger) => void;
  /** True for an unpaid operator — for lock icons/rendering decisions. */
  isGated: boolean;
}

const PaywallContext = createContext<PaywallContextType | undefined>(undefined);

export function PaywallProvider({ children }: { children: ReactNode }) {
  const { isEntitled, isLoading } = useOperatorEntitlement();
  const [openTrigger, setOpenTrigger] = useState<PaywallTrigger | null>(null);

  const guardCreate = useCallback(
    (trigger: PaywallTrigger, action: () => void) => {
      if (isEntitled) {
        action();
        return;
      }
      if (isLoading) return; // no verdict yet — never flash a paywall
      setOpenTrigger(trigger);
    },
    [isEntitled, isLoading]
  );

  const openPaywall = useCallback((trigger: PaywallTrigger) => {
    setOpenTrigger(trigger);
  }, []);

  const value = useMemo(
    () => ({ guardCreate, openPaywall, isGated: !isEntitled && !isLoading }),
    [guardCreate, openPaywall, isEntitled, isLoading]
  );

  return (
    <PaywallContext.Provider value={value}>
      {children}
      {openTrigger && (
        <PaywallDialog
          open
          trigger={openTrigger}
          onOpenChange={(open) => {
            if (!open) setOpenTrigger(null);
          }}
        />
      )}
    </PaywallContext.Provider>
  );
}

export function usePaywall(): PaywallContextType {
  const ctx = useContext(PaywallContext);
  if (!ctx) throw new Error("usePaywall must be used within PaywallProvider");
  return ctx;
}
